import React from 'react'
import {observable, action, makeObservable} from 'mobx'
import {observer} from 'mobx-react-lite'
import {SvgIcon, SvgIconProps} from '@mui/material'
import {ClassNameMap} from '@mui/material'
import {DOMParser, XMLSerializer} from 'xmldom'
import {makeStyles} from '@oc/styles'

const useStyles = makeStyles({
    rotateRight: {
        transform: 'rotate(90deg)'
    },
    rotateDown: {
        transform: 'rotate(180deg)'
    },
    rotateLeft: {
        transform: 'rotate(270deg)'
    }
})

const domParser = new DOMParser()
const xmlSerializer = new XMLSerializer()

interface IconPropsFull extends Pick<SvgIconProps, 'style' | 'color' | 'htmlColor' | 'className' | 'viewBox'> {
    size?: `${number}px` | number
    fontSize?: `${number}rem` | `${number}px` | 'default' | 'inherit' | 'large' | 'medium' | 'small' | number
    rotate?: 'right' | 'left' | 'down'
}

interface IconFontSizeProps extends Omit<IconPropsFull, 'size'> {
    size?: undefined
}
interface IconSizeProps extends Omit<IconPropsFull, 'fontSize'> {
    fontSize?: undefined
}
export type IconProps = IconFontSizeProps | IconSizeProps
export type IconBaseProps = IconProps & {url: string; 'data-cy'?: string}

export function getIconClassName(
    styles: ClassNameMap<'rotateRight' | 'rotateDown' | 'rotateLeft'>,
    rotate?: 'right' | 'left' | 'down'
): string | undefined {
    switch (rotate) {
        case 'right':
            return styles.rotateRight
        case 'left':
            return styles.rotateLeft
        case 'down':
            return styles.rotateDown
        default:
            return undefined
    }
}

function svgCleanUp(input: string): OcIconInfo {
    const document = domParser.parseFromString(input, 'image/svg+xml')
    const viewBox = document.documentElement.attributes.getNamedItem('viewBox')?.nodeValue

    const titleElements = document.getElementsByTagName('title')
    for (let i = 0; i < titleElements.length; i++) {
        const child = titleElements[i]
        document.removeChild(child)
    }

    const styleElements = document.getElementsByTagName('style')
    for (let i = 0; i < styleElements.length; i++) {
        const child = styleElements[i]
        document.removeChild(child)
    }

    return {
        innerHtml: xmlSerializer.serializeToString(document),
        viewBox: viewBox
    }
}

interface OcIconInfo {
    innerHtml: string
    viewBox?: string | null
}

class IconBaseState {
    @observable public iconHtml = ''
    @observable public viewBox?: string | null = undefined
    private static icons: Map<string, Promise<OcIconInfo>> = new Map()

    constructor() {
        makeObservable(this)
    }

    @action.bound
    public async loadIcon(url: string) {
        let iconPromise = IconBaseState.icons.get(url)
        if (!iconPromise) {
            iconPromise = this.loadIconInternal(url)
            IconBaseState.icons.set(url, iconPromise)
        }

        const icon = await iconPromise
        this.iconHtml = icon?.innerHtml || ''
        this.viewBox = icon?.viewBox
    }

    private async loadIconInternal(url: string): Promise<OcIconInfo> {
        let attemptsLeft = 3
        while (true) {
            try {
                const iconResponse = await fetch(url)
                return svgCleanUp(await iconResponse.text())
            } catch (err) {
                attemptsLeft--
                if (attemptsLeft <= 0) {
                    throw err
                }
            }
        }
    }
}

function _IconBase({
    url,
    fontSize,
    color,
    className,
    rotate,
    htmlColor,
    viewBox,
    size,
    style,
    'data-cy': dataCy
}: IconBaseProps) {
    const {classes, cx} = useStyles()
    const [state] = React.useState(() => new IconBaseState())

    const getClassName = React.useMemo(() => {
        return getIconClassName(classes, rotate)
    }, [classes, rotate])

    React.useEffect(() => {
        state.loadIcon(url)
    }, [state, url])

    // store observable in variable so it is rerendered when state.loadIcon finishes
    const iconHtml = state.iconHtml

    const svgStyles: React.CSSProperties = React.useMemo(() => {
        if (size) {
            return {width: size, height: size, ...style}
        } else if (fontSize) {
            return {fontSize: fontSize, ...style}
        }
        return {width: '24px', height: '24px', ...style}
    }, [size, fontSize, style])

    return (
        <SvgIcon
            color={color}
            className={cx(className, getClassName)}
            style={svgStyles}
            htmlColor={htmlColor}
            viewBox={state.viewBox || viewBox}
            component={(props: any) => {
                const {children: _children, ...newProps} = props

                return (
                    <svg
                        {...newProps}
                        dangerouslySetInnerHTML={{
                            __html: iconHtml
                        }}
                    />
                )
            }}
            data-cy={dataCy}
        />
    )
}

export const IconBase = observer(_IconBase)
